import React, { useEffect } from 'react';
import { useTestPipeline } from '../../hooks/useTestPipeline';
import AbilitySectionMenu from './AbilitySectionMenu';
import AbilityQuestionView from './AbilityQuestionView';
import AbilitySuiteCover from './AbilitySuiteCover';
import AbilityPreparationPage from './AbilityPreparationPage';
import AbilityChapterComplete from './AbilityChapterComplete';
import FloatingCalculator from './FloatingCalculator';
import {
  areAllCoreAbilitiesComplete,
  getPlayableAbilityPapers,
  getPlayablePaperIndex,
  normalizeAbilitySlug
} from '../../data/abilityPapers';
import { getPaperProgress } from '../../utils/testUtils';

function TestPipeline({
  papers,
  completedSlugs,
  onRefreshCompletions,
  onClearCompletions,
  usedDemoFallback
}) {
  const {
    t,
    lang,
    confirmAsync,
    ConfirmDialog,
    activePaper,
    phase,
    questionIndex,
    answers,
    remainingSeconds,
    timerExpired,
    chapterResult,
    progressBySlug,
    totalQuestions,
    totalPaperQuestions,
    paperQuestionIndex,
    allQuestionsAnswered,
    currentItem,
    selectPaper,
    retakeAbilityTest,
    resetSession,
    beginSuite,
    openSection,
    goToNextSection,
    pauseToSectionMenu,
    pauseToMenu,
    finishSection,
    submitEntireTest,
    paperTitle,
    paperInstruction,
    sectionTitle,
    showSectionTitle,
    usesSectionMenu,
    hasNextSection,
    isPenultimateSection,
    currentPassage,
    selectOption,
    goPrev,
    goNext,
    isLast,
    currentAnswered
  } = useTestPipeline();

  const isPaperComplete = (paper) => completedSlugs?.has?.(normalizeAbilitySlug(paper.slug));
  const playablePapers = getPlayableAbilityPapers(papers);
  const firstUnfinishedPaper = playablePapers.find((paper) => !isPaperComplete(paper));
  const firstUnfinishedIndex = firstUnfinishedPaper
    ? papers.findIndex(
        (paper) =>
          normalizeAbilitySlug(paper.slug) === normalizeAbilitySlug(firstUnfinishedPaper.slug)
      )
    : -1;
  const activePlayableIndex = getPlayablePaperIndex(papers, activePaper);
  const nextPlayablePaper =
    activePlayableIndex >= 0 && activePlayableIndex < playablePapers.length - 1
      ? playablePapers[activePlayableIndex + 1]
      : null;
  const suiteFinishedThisSession =
    activePlayableIndex >= 0 && activePlayableIndex === playablePapers.length - 1;

  const startFirstUnfinishedPaper = () => {
    if (firstUnfinishedPaper) selectPaper(firstUnfinishedPaper);
  };

  const handleRetakeAbilityTest = () => {
    void retakeAbilityTest(papers, onClearCompletions);
  };

  const handleRestartAbilityTest = () => {
    void retakeAbilityTest(papers, onClearCompletions, {
      confirmKey: 'test.confirmRestartAbilityTest'
    });
  };

  const allAbilitiesComplete = areAllCoreAbilitiesComplete(completedSlugs);

  useEffect(() => {
    if (phase === 'complete') {
      void onRefreshCompletions?.();
    }
  }, [phase, onRefreshCompletions]);

  const hasAnyProgress =
    (completedSlugs?.size ?? 0) > 0 ||
    papers.some((paper) => getPaperProgress(paper, progressBySlug).answered > 0);
  const hasOngoingTest = hasAnyProgress && !allAbilitiesComplete;

  const handleSelectPart = (paper) => {
    selectPaper(paper);
  };

  const moveToNextSection = async () => {
    if (!nextPlayablePaper) return;
    await onRefreshCompletions?.();
    selectPaper(nextPlayablePaper);
  };

  const handleBackFromQuestions = () => {
    void (async () => {
      if (usesSectionMenu) {
        if (!(await confirmAsync(t('test.confirmBackToSections')))) return;
        pauseToSectionMenu();
      } else {
        if (!(await confirmAsync(t('test.confirmPauseToMenu')))) return;
        pauseToMenu();
      }
    })();
  };

  const handleBackToPaperGrid = () => {
    void (async () => {
      if (!(await confirmAsync(t('test.confirmPauseToMenu')))) return;
      pauseToMenu();
    })();
  };

  const isFullBleed =
    phase === 'menu' || phase === 'sectionMenu' || phase === 'suiteCover';
  const isScrollableCover = phase === 'suiteCover' || phase === 'menu';

  const isQuantitativeReasoningChapter =
    Boolean(activePaper) &&
    normalizeAbilitySlug(activePaper.slug) === 'quantitative-reasoning' &&
    (phase === 'questions' || phase === 'sectionMenu');

  return (
    <>
      {ConfirmDialog}
      <FloatingCalculator open={isQuantitativeReasoningChapter} t={t} />
      <div
        className={`bg-canvas ${isFullBleed ? 'md:p-0 p-0' : 'p-4 md:p-8'} ${
          isScrollableCover
            ? 'min-h-[calc(100dvh-4rem)] flex flex-col overflow-y-auto'
            : 'min-h-[60vh]'
        }`}
      >
        <div
          className={`${isFullBleed ? 'max-w-6xl mx-auto w-full' : 'max-w-3xl mx-auto'} ${
            isScrollableCover ? 'flex flex-1 flex-col min-h-0' : ''
          }`}
        >
          {usedDemoFallback && (
            <div
              className={`mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm text-justify ${
                isFullBleed ? 'mx-4 md:mx-8 mt-4' : ''
              }`}
            >
              {t('test.demoFallback')}
            </div>
          )}

          {phase === 'menu' && papers.length === 0 && (
            <div className="bg-surface rounded-lg shadow-sm p-6 md:p-8 text-center text-ink-muted">
              {t('test.noPapers')}
            </div>
          )}

          {phase === 'suiteCover' && papers.length > 0 && (
            <AbilitySuiteCover
              t={t}
              onBegin={beginSuite}
              onRestart={handleRestartAbilityTest}
              hasOngoingTest={hasOngoingTest}
            />
          )}

          {phase === 'menu' && papers.length > 0 && (
            <AbilityPreparationPage
              t={t}
              lang={lang}
              papers={papers}
              completedSlugs={completedSlugs}
              progressBySlug={progressBySlug}
              firstUnfinishedIndex={firstUnfinishedIndex}
              onSelectPart={handleSelectPart}
              onPrepared={startFirstUnfinishedPaper}
              onRestart={handleRestartAbilityTest}
              onRetake={handleRetakeAbilityTest}
              hasOngoingTest={hasOngoingTest}
              disabled={allAbilitiesComplete}
            />
          )}

          {phase === 'sectionMenu' && activePaper && usesSectionMenu && (
            <AbilitySectionMenu
              t={t}
              lang={lang}
              paperTitle={paperTitle}
              paperInstruction={paperInstruction}
              sections={activePaper.sections || []}
              answers={answers}
              remainingSeconds={remainingSeconds}
              allQuestionsAnswered={allQuestionsAnswered}
              onOpenSection={openSection}
              onBackToPaperGrid={handleBackToPaperGrid}
              onSubmitEntireTest={() => {
                void (async () => {
                  if (!(await confirmAsync(t('test.confirmSubmitTest')))) return;
                  await submitEntireTest();
                  await onRefreshCompletions?.();
                })();
              }}
            />
          )}

          {phase === 'questions' && activePaper && currentItem && (
            <AbilityQuestionView
              t={t}
              lang={lang}
              paperTitle={paperTitle}
              sectionTitle={sectionTitle}
              paperInstruction={paperInstruction}
              showSectionTitle={showSectionTitle}
              currentPassage={currentPassage}
              currentItem={currentItem}
              questionIndex={questionIndex}
              totalQuestions={totalQuestions}
              paperQuestionIndex={paperQuestionIndex}
              totalPaperQuestions={totalPaperQuestions}
              remainingSeconds={remainingSeconds}
              answers={answers}
              currentAnswered={currentAnswered}
              isLast={isLast}
              hasNextSection={hasNextSection}
              isPenultimateSection={isPenultimateSection}
              usesSectionMenu={usesSectionMenu}
              onBackFromQuestions={handleBackFromQuestions}
              onSelectOption={selectOption}
              onPrev={goPrev}
              onNext={goNext}
              onFinishSection={() => finishSection()}
              onGoToNextSection={goToNextSection}
            />
          )}

          {phase === 'complete' && (
            <AbilityChapterComplete
              t={t}
              lang={lang}
              chapter={activePaper}
              chapterResult={chapterResult}
              timerExpired={timerExpired}
              hasNextChapter={Boolean(nextPlayablePaper)}
              onContinue={() => {
                void (async () => {
                  if (nextPlayablePaper) {
                    await moveToNextSection();
                    return;
                  }
                  if (suiteFinishedThisSession) {
                    resetSession();
                    await onRefreshCompletions?.();
                  }
                })();
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default TestPipeline;
